import type { AttendanceReportRow } from "../types/attendance";

export const ALL_FILTER = "All";

export const PURPOSE_LABELS: Record<string, string> = {
  test: "Test",
  others: "Others",
  onsite: "Onsite",
};

export const humanizePurpose = (value: string) => PURPOSE_LABELS[value] ?? value;

export function encodeAttendanceNameForPath(name: string): string {
  const trimmed = name.trim();
  return encodeURIComponent(trimmed || "attendance");
}

export function attendanceStudentReportPath(name: string): string {
  return `/admin/attendance/report/${encodeAttendanceNameForPath(name)}`;
}

export function parseBatchNames(batch: string): string[] {
  return batch
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);
}

export function reportRowBatchNames(row: AttendanceReportRow): string[] {
  return parseBatchNames(row.batch);
}

export function uniqueSorted(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
  );
}

export function mergeReportRowsBySession(
  rows: AttendanceReportRow[],
): AttendanceReportRow[] {
  const order: string[] = [];
  const bySession = new Map<string, AttendanceReportRow>();

  for (const row of rows) {
    const existing = bySession.get(row.attendanceSessionId);
    if (!existing) {
      const batchIds = row.batchIds?.length
        ? [...row.batchIds]
        : row.batchId
          ? [row.batchId]
          : [];
      bySession.set(row.attendanceSessionId, {
        ...row,
        batchIds,
        batchId: batchIds.length === 1 ? batchIds[0] : row.batchId,
      });
      order.push(row.attendanceSessionId);
      continue;
    }

    const names = new Set(reportRowBatchNames(existing));
    for (const name of reportRowBatchNames(row)) names.add(name);
    const ids = new Set(existing.batchIds ?? []);
    for (const id of row.batchIds ?? []) ids.add(id);
    if (row.batchId) ids.add(row.batchId);
    existing.batch = Array.from(names).join(", ");
    existing.batchIds = Array.from(ids);
    existing.batchId =
      existing.batchIds.length === 1 ? existing.batchIds[0] : undefined;
  }

  return order.map((sessionId, index) => ({
    ...bySession.get(sessionId)!,
    slNo: index + 1,
  }));
}

export function rowMatchesFilters(
  row: AttendanceReportRow,
  course: string,
  batch: string,
  purpose: string,
): boolean {
  if (course !== ALL_FILTER && row.course !== course) return false;
  if (purpose !== ALL_FILTER && row.purpose !== purpose) return false;
  if (batch !== ALL_FILTER && !reportRowBatchNames(row).includes(batch)) {
    return false;
  }
  return true;
}

export function batchIdForName(
  row: AttendanceReportRow,
  batchName: string,
): string | undefined {
  const names = reportRowBatchNames(row);
  const ids = row.batchIds ?? (row.batchId ? [row.batchId] : []);
  const index = names.indexOf(batchName);
  if (index >= 0 && ids[index]) return ids[index];
  if (names.length === 1 && ids[0]) return ids[0];
  return undefined;
}
